import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { MarketABI } from "@/lib/contracts";

// Get user's bets for a market
export function useUserBets(
  marketAddress: `0x${string}` | undefined,
  userAddress: `0x${string}` | undefined
) {
  return useReadContract({
    address: marketAddress,
    abi: MarketABI,
    functionName: "getUserBets",
    args: userAddress ? [userAddress] : undefined,
    query: {
      enabled: !!marketAddress && !!userAddress,
    },
  });
}

// Get unclaimed winnings for a user
export function useUnclaimedWinnings(
  marketAddress: `0x${string}` | undefined,
  userAddress: `0x${string}` | undefined
) {
  return useReadContract({
    address: marketAddress,
    abi: MarketABI,
    functionName: "getUnclaimedWinnings",
    args: userAddress ? [userAddress] : undefined,
    query: {
      enabled: !!marketAddress && !!userAddress,
    },
  });
}

// Get potential payout for a bet
export function usePotentialPayout(
  marketAddress: `0x${string}` | undefined,
  outcome: boolean,
  amount: bigint
) {
  return useReadContract({
    address: marketAddress,
    abi: MarketABI,
    functionName: "getPotentialPayout",
    args: [outcome, amount],
    query: {
      enabled: !!marketAddress && amount > 0n,
    },
  });
}

// Hook for placing a bet
export function usePlaceBet() {
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const placeBet = (
    marketAddress: `0x${string}`,
    outcome: boolean,
    amount: bigint
  ) => {
    writeContract({
      address: marketAddress,
      abi: MarketABI,
      functionName: "placeBet",
      args: [outcome, amount],
    });
  };

  return {
    placeBet,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
    reset,
  };
}

// Hook for claiming winnings
export function useClaimWinnings() {
  const { writeContract, data: hash, isPending, error, reset } = useWriteContract();

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  });

  const claimWinnings = (marketAddress: `0x${string}`) => {
    writeContract({
      address: marketAddress,
      abi: MarketABI,
      functionName: "claimWinnings",
    });
  };

  return {
    claimWinnings,
    hash,
    isPending,
    isConfirming,
    isSuccess,
    error,
    reset,
  };
}
